// app/pages/CauseOfCalls.jsx
import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { causeOfCallService } from '../services/causeOfCall.service';
import { getClients, getClientSchemes } from '../services/customer.service';
import CauseTree from '../components/causeOfCalls/CauseTree';
import CauseModal from '../components/causeOfCalls/CauseModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useDialog } from '../contexts/DialogContext';

const CauseOfCalls = () => {
    const { toast } = useDialog();
    const [causes, setCauses] = useState([]);
    const [allCauses, setAllCauses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCause, setEditingCause] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteCauseId, setDeleteCauseId] = useState(null);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    
    // Client and scheme selection
    const [clients, setClients] = useState([]);
    const [schemes, setSchemes] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedSchemeId, setSelectedSchemeId] = useState('');
    const [loadingClients, setLoadingClients] = useState(true);
    const [loadingSchemes, setLoadingSchemes] = useState(false);

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        if (selectedClientId) {
            fetchSchemes(selectedClientId);
        } else {
            setSchemes([]);
            setSelectedSchemeId('');
        }
    }, [selectedClientId]);

    useEffect(() => {
        if (selectedSchemeId) {
            fetchCauses();
        } else {
            setCauses([]);
            setAllCauses([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSchemeId]);

    const fetchClients = async () => {
        setLoadingClients(true);
        try {
            const response = await getClients({ is_active: true });
            setClients(response || []);
            
            // Auto-select if only one client
            if (response && response.length === 1) {
                setSelectedClientId(response[0].id);
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    };

    const fetchSchemes = async (clientId) => {
        if (!clientId) {
            setSchemes([]);
            return;
        }
        
        setLoadingSchemes(true);
        try {
            const response = await getClientSchemes(clientId, { is_active: true });
            setSchemes(response || []);
            
            // Auto-select if only one scheme
            if (response && response.length === 1) {
                setSelectedSchemeId(response[0].id);
            }
        } catch (error) {
            console.error('Error fetching schemes:', error);
            setSchemes([]);
        } finally {
            setLoadingSchemes(false);
        }
    };

    const fetchCauses = async () => {
        if (!selectedSchemeId) return;
        
        setLoading(true);
        try {
            // Fetch root causes
            const data = await causeOfCallService.getCauseOfCalls(selectedSchemeId, null);
            setCauses(data || []);

            // Fetch all causes for parent selection
            await fetchAllCauses();
        } catch (error) {
            console.error('Error fetching causes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCauses = async () => {
        if (!selectedSchemeId) return;
        
        const all = [];
        const fetchRecursive = async (parentId = null) => {
            const res = await causeOfCallService.getCauseOfCalls(selectedSchemeId, parentId);
            all.push(...(res || []));
            for (const cause of (res || [])) {
                if (cause.has_children) {
                    await fetchRecursive(cause.id);
                }
            }
        };
        await fetchRecursive();
        setAllCauses(all);
    };

    const toggleExpanded = async (causeId) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(causeId)) {
            newExpanded.delete(causeId);
        } else {
            newExpanded.add(causeId);
            // Fetch children if not already loaded
            const cause = findCause(causes, causeId);
            if (cause && cause.has_children && !cause.children) {
                try {
                    const data = await causeOfCallService.getCauseOfCalls(selectedSchemeId, causeId);
                    cause.children = data || [];
                    setCauses([...causes]);
                } catch (error) {
                    console.error('Error fetching children:', error);
                }
            }
        }
        setExpandedNodes(newExpanded);
    };

    const findCause = (causesList, causeId) => {
        for (const cause of causesList) {
            if (cause.id === causeId) return cause;
            if (cause.children) {
                const found = findCause(cause.children, causeId);
                if (found) return found;
            }
        }
        return null;
    };

    const handleCreate = () => {
        if (!selectedSchemeId) {
            toast.error('Please select a scheme first');
            return;
        }
        setEditingCause(null);
        setShowModal(true);
    };

    const handleEdit = (cause) => {
        setEditingCause(cause);
        setShowModal(true);
    };

    const handleDelete = (causeId) => {
        setDeleteCauseId(causeId);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await causeOfCallService.deleteCauseOfCall(deleteCauseId);
            fetchCauses();
            setShowDeleteConfirm(false);
        } catch (error) {
            console.error('Error deleting cause:', error);
        }
    };

    const handleSave = async (causeData) => {
        try {
            if (editingCause) {
                await causeOfCallService.updateCauseOfCall(editingCause.id, causeData);
            } else {
                await causeOfCallService.createCauseOfCall(causeData);
            }
            setShowModal(false);
            fetchCauses();
        } catch (error) {
            console.error('Error saving cause:', error);
            throw error;
        }
    };

    const handleClientChange = (e) => {
        setSelectedClientId(e.target.value);
        setSelectedSchemeId('');
    };

    const handleSchemeChange = (e) => {
        setSelectedSchemeId(e.target.value);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white flex justify-between items-center bxshadowhd py-3 px-3 mrgall border">
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 ">
                    Cause of Calls
                </h1>
                <div className="flex gap-2">
                    <button
                        onClick={handleCreate}
                        disabled={!selectedSchemeId}
                        className="flex items-center gap-2 px-4 py-2 bg-scarlet-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4" />
                        Add Cause
                    </button>
                    <button
                        onClick={fetchCauses}
                        disabled={!selectedSchemeId}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Client and Scheme Selection */}
            <div className="bg-white rounded-lg bxshadowhd p-4 mrgall border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Client Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedClientId}
                            onChange={handleClientChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loadingClients}
                        >
                            <option value="">- Select Client -</option>
                            {loadingClients ? (
                                <option>Loading...</option>
                            ) : (
                                clients.map(client => (
                                    <option key={client.id} value={client.id}>
                                        {client.client_name}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>

                    {/* Scheme Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Scheme <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedSchemeId}
                            onChange={handleSchemeChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={!selectedClientId || loadingSchemes}
                        >
                            <option value="">- Select Scheme -</option>
                            {loadingSchemes ? (
                                <option>Loading...</option>
                            ) : (
                                schemes.map(scheme => (
                                    <option key={scheme.id} value={scheme.id}>
                                        {scheme.scheme_name}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg bxshadowhd p-4 mrgall border">
                {!selectedSchemeId ? (
                    <div className="p-6 text-center text-gray-500">
                        Please select a client and scheme to view cause of calls.
                    </div>
                ) : (
                    <>
                        <div className="p-3 bg-blue-100 mrgall rounded-xl mb-4">
                            <h3 className="font-semibold text-gray-900">Cause Hierarchy</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Click on the arrow to expand/collapse categories
                            </p>
                        </div>

                        {loading ? (
                            <LoadingSpinner />
                        ) : causes.length === 0 ? (
                            <div className="p-6 text-center text-gray-500">
                                No causes found for this scheme. Click "Add Cause" to create one.
                            </div>
                        ) : (
                            <CauseTree
                                causes={causes}
                                expandedNodes={expandedNodes}
                                onToggleExpand={toggleExpanded}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        )}
                    </>
                )}
            </div>

            <CauseModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSave={handleSave}
                cause={editingCause}
                allCauses={allCauses}
                schemeId={selectedSchemeId}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Cause"
                message="Are you sure you want to delete this cause? This will also delete all child causes."
            />
        </div>
    );
};

export default CauseOfCalls;